/**
 * The trainer's own Pokémon GO friend code, editable.
 *
 * Everything else in the Settings account block is read-only, on the principle
 * that the profile screenshot is the single source of truth. The code is the
 * exception because the screenshot cannot keep it current — codes rotate — and
 * raid hosting refuses a host without one, since a raid whose host cannot be
 * added in Pokémon GO is a raid nobody can join.
 *
 * The stored value is twelve bare digits; the field shows and accepts the
 * spaced form because that is how Pokémon GO displays it and how it lands on the
 * clipboard when a trainer copies it out of the game. Anything the trainer
 * pastes is accepted and normalised rather than rejected for its spacing.
 *
 * It is never shown to anyone else. The only thing that reads it is the copy
 * button in a lobby its owner opened, and the endpoint behind that checks live
 * membership before it hands it over.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

/** 12 bare digits → "1234 5678 9012". Anything else passes through unchanged. */
export function formatFriendCode(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 12) return raw;
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
}

export function TrainerCodeField() {
  const { toast } = useToast();
  const [value, setValue] = useState("");
  const [dirty, setDirty] = useState(false);

  const codeQuery = useQuery<{ friendCode: string | null }>({
    queryKey: ["/api/account/trainer"],
  });

  // Seed the field once from the server, and never again while the trainer is
  // mid-edit: a refetch landing under their fingers would discard what they
  // typed.
  useEffect(() => {
    if (dirty) return;
    const code = codeQuery.data?.friendCode;
    if (typeof code === "string") setValue(formatFriendCode(code));
  }, [codeQuery.data, dirty]);

  const save = useMutation({
    mutationFn: async (friendCode: string) => {
      const res = await apiRequest("PATCH", "/api/account/trainer", { friendCode });
      return res.json();
    },
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["/api/account/trainer"] });
      toast({ title: "Trainer code saved" });
    },
    onError: () => {
      toast({
        title: "Not saved",
        description: "A trainer code is exactly 12 digits.",
      });
    },
  });

  const digits = value.replace(/\D/g, "");
  const valid = digits.length === 12;

  return (
    <div className="mt-1.5" data-testid="settings-trainer-code">
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => {
            setDirty(true);
            setValue(formatFriendCode(e.target.value));
          }}
          placeholder="1234 5678 9012"
          aria-label="Pokémon GO trainer code"
          className="flex-1 min-w-0 rounded-xl px-3 py-2 text-sm font-mono tabular-nums"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
          }}
          data-testid="input-trainer-code"
        />
        <Button
          size="sm"
          disabled={!valid || !dirty || save.isPending}
          onClick={() => save.mutate(digits)}
          data-testid="button-save-trainer-code"
        >
          {save.isPending ? "Saving" : <Check className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground leading-snug">
        Copy it from Pokémon GO and paste it here. Only shared with people who
        join a raid you host, so they can add you.
      </p>
    </div>
  );
}
